import { Button, message } from 'antd';
import { isEmpty } from 'lodash';
import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PlusOutlined } from '@ant-design/icons';
import { AD_PLAYS } from '@utils/constants';
import { DIMENSION_LIMIT } from '@store/trends/base/reducer';
import { adContentMetricsSelector } from '@store/common/selectors';
import { allDimensionsListSelector } from '@store/common/dimensionList/selectors';
import { setGlobalOrderByMetric } from '@store/trends/base/actions';
import { globalOrderBySelector } from '@store/trends/base/selectors';
import { activeDashboardSelector } from '@src/store/tempDashboard/selectors';
import useDimensionsCategories from '../hooks/useDimensionsCategories';
import DimensionPopupModal from './DimensionPopupModal';
import { DIMENSION_POPUP_OK_TEXT, DIMENSION_POPUP_TITLE, hasAdDimensions } from './utils';
import { useDimensionOperations } from '../hooks/useDimensionOperations';
import { useCurrentLimitation } from '@src/hooks/useCurrentLimitation';

const isAdModule = window.conviva.module === 'AI';
const defaultAIMetric = AD_PLAYS;

const DimensionsSelectorPopup = ({ buttonText = 'Dimensions', excludeCategories = [] }) => {
  const dispatch = useDispatch();
  const allDimensions = useSelector(allDimensionsListSelector) as any[];
  const {
    selectedDimensions: selectedDimensionsFromStore,
    setSelectedDimensions: setSelectedDimensionsInStore,
  } = useDimensionOperations();
  const globalOrderBy = useSelector(globalOrderBySelector);
  const adContentMetrics = useSelector(adContentMetricsSelector);
  const [selectedDimensionsList, setSelectedDimensionsList] = useState(selectedDimensionsFromStore);
  const searchRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const { dataScope }: any = useSelector(activeDashboardSelector);
  const limitation = useCurrentLimitation();

  const openPopup = () => {
    setVisible(true);
    // focus on search box after a delay
    // ref init + visual feedback after popup animation
    setTimeout(() => {
      if (searchRef.current) {
        (searchRef.current as any).focus({
          cursor: 'start',
        });
      }
    }, 500);
  };

  const closePopup = () => {
    setVisible(false);
    setSelectedDimensionsList(selectedDimensionsFromStore);
  };

  const onOk = () => {
    if (selectedDimensionsList && selectedDimensionsList.length > 0) {
      setVisible(false);
      setSelectedDimensionsInStore(selectedDimensionsList);
      if (
        !isAdModule &&
        hasAdDimensions(selectedDimensionsList, dimensionsCategories) &&
        !(globalOrderBy in adContentMetrics)
      )
        dispatch(setGlobalOrderByMetric(defaultAIMetric));
    }
  };

  const onClear = () => {
    setSelectedDimensionsList([]);
  };

  const dimensionsCategories = useDimensionsCategories({
    allDimensions,
    selectedDimensionsList,
    excludeCategories,
    excludeDimensions: limitation?.dimensions?.hiddenKeys,
    hiddenEmptyCategory: !!dataScope,
  });

  useEffect(() => {
    setSelectedDimensionsList(selectedDimensionsFromStore);
  }, [selectedDimensionsFromStore]);

  const onChange = (item) => {
    const dimensionKey = item.target.value;
    if (selectedDimensionsList.includes(dimensionKey)) {
      setSelectedDimensionsList(selectedDimensionsList.filter((value) => value !== dimensionKey));
    } else {
      setSelectedDimensionsList([dimensionKey, ...selectedDimensionsList]);
    }
  };

  useEffect(() => {
    if (selectedDimensionsList.length > DIMENSION_LIMIT.max) {
      message.warning(`You can select a maximum of ${DIMENSION_LIMIT.max} dimensions.`);
      setSelectedDimensionsList(selectedDimensionsList.slice(1, DIMENSION_LIMIT.max + 1)); // why?
    }
  }, [selectedDimensionsList]);

  if (isEmpty(allDimensions)) {
    return null;
  }

  return (
    <>
      <Button onClick={openPopup} icon={<PlusOutlined />}>
        {buttonText}
      </Button>
      <DimensionPopupModal
        title={DIMENSION_POPUP_TITLE}
        visible={visible}
        onCancel={closePopup}
        onOk={onOk}
        okText={DIMENSION_POPUP_OK_TEXT}
        dimensionsCategories={dimensionsCategories}
        selectedDimensionsList={selectedDimensionsList}
        onChange={onChange}
        onClear={onClear}
        searchRef={searchRef}
      />
    </>
  );
};

export default DimensionsSelectorPopup;
